
import { useEffect, useState } from "react";
import { 
  toggleResponsiveMenu, 
  initWowAnimations, 
  handleSplashScreen, 
  initScrollRestoration,
  setupGlobalFunctions,
  preloadCriticalImages,
  optimizeScriptLoading,
  preventLayoutShifts,
  setupOptimizedEventListeners,
  setupPerformanceMonitoring,
  optimizeImages
} from "../utils";

/**
 * Custom hook for centralizing page initialization logic
 * @param title - The page title to set
 * @param options - Configuration options for the initialization
 */
export const usePageInitialization = (
  title: string,
  options: {
    handleSplash?: boolean;
    initSwiper?: boolean;
  } = { handleSplash: true, initSwiper: true }
) => {
  const [scriptsInitialized, setScriptsInitialized] = useState(false);

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    // Setup global functions first
    setupGlobalFunctions();
    
    // Handle splash screen immediately if enabled
    if (options.handleSplash) {
      handleSplashScreen();
    }
    
    // Initialize scroll restoration
    initScrollRestoration();
    
    // Preload critical images
    preloadCriticalImages();
    
    // Optimize script loading
    optimizeScriptLoading();
    
    // Setup optimized event listeners
    setupOptimizedEventListeners();
    
    // Optimize images
    optimizeImages();
    
    // Setup performance monitoring
    setupPerformanceMonitoring();

    // Set up an interval to check if scripts are loaded
    console.log("Setting up interval to check for script loading");
    const checkScriptsInterval = setInterval(() => {
      // Check if Swiper and WOW are available
      if ((window as any).WOW && (!options.initSwiper || (window as any).Swiper)) {
        clearInterval(checkScriptsInterval);
        
        // Initialize scripts once they're available
        let swiperInstance = null;
        if (options.initSwiper) {
          // We'll use the modern carousel now, but keep this for legacy support
          // swiperInstance = initSwiperCarousel();
        }
        const wowInstance = initWowAnimations();
        
        if ((!options.initSwiper || swiperInstance) && wowInstance) {
          console.log("All scripts initialized successfully");
          setScriptsInitialized(true);
          
          // Prevent layout shifts after initialization
          preventLayoutShifts();
        }
      }
    }, 100);

    // Use shorter timeout with decreasing check frequency
    const timeoutIds = [
      setTimeout(() => checkScripts(200), 2000),
      setTimeout(() => checkScripts(500), 5000),
      setTimeout(() => checkScripts(1000, true), 8000)
    ];
    
    function checkScripts(interval: number, isFinal = false) {
      if (!scriptsInitialized) {
        clearInterval(checkScriptsInterval);
        
        const newInterval = setInterval(() => {
          if ((window as any).WOW && (!options.initSwiper || (window as any).Swiper)) {
            clearInterval(newInterval);
            console.log(`Scripts loaded after retry with ${interval}ms interval`);
            
            let swiperInstance = null;
            if (options.initSwiper) {
              // We'll use the modern carousel now, but keep this for legacy support
              // swiperInstance = initSwiperCarousel();
            }
            const wowInstance = initWowAnimations();
            
            if ((!options.initSwiper || swiperInstance) && wowInstance) {
              setScriptsInitialized(true);
              preventLayoutShifts();
            }
          } else if (isFinal) {
            clearInterval(newInterval);
            console.warn("Scripts not loaded after timeout, trying to initialize anyway");
            if (options.initSwiper) {
              // Legacy support
              // initSwiperCarousel();
            }
            initWowAnimations();
            setScriptsInitialized(true);
            preventLayoutShifts();
          }
        }, interval);
      }
    }

    return () => {
      clearInterval(checkScriptsInterval);
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [options.handleSplash, options.initSwiper, scriptsInitialized]);

  return { scriptsInitialized };
};
